import React, { useEffect, useState } from "react";
import { matchesMember } from "../lib/memberSearch";
import { Play, ExternalLink, X, Loader2, Radio, Send, Share2, Repeat2, Search } from "lucide-react";
import api, { streamThumb, CATEGORY_LABELS } from "../lib/api";
import { useExternalLink } from "../context/ExternalLinkContext";
import { useCrypto } from "../context/CryptoContext";
import { DomainPicker } from "./DomainPicker";
import { Avatar } from "./Media";

// Build the plaintext public reference we ride along with an E2E post/DM.
export const toStreamRef = (item) => ({
  item_id: item.id || item.item_id,
  kind: item.kind,
  title: item.title,
  thumbnail: item.thumbnail || null,
  url: item.url,
  video_id: item.video_id || null,
  source_name: item.source_name,
  excerpt: item.excerpt || null,
});

// The youtube-nocookie player used across Stream + Feed + DMs.
export const StreamPlayerModal = ({ item, onClose }) => {
  const { openExternal } = useExternalLink();
  return (
    <div className="fixed inset-0 z-[130] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="stream-player-modal" onClick={onClose}>
      <div className="bg-[#0D0D0D] border border-white/10 max-w-3xl w-full fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="min-w-0">
            <div className="font-body text-[10px] tracking-[0.2em] uppercase text-[#14A89A] truncate">{item.source_name}</div>
            <h3 className="font-heading text-sm text-white truncate">{item.title}</h3>
          </div>
          <button onClick={onClose} data-testid="stream-player-close" className="text-zinc-500 hover:text-white shrink-0 ml-3"><X size={20} /></button>
        </div>
        <div className="aspect-video bg-black">
          <iframe
            title={item.title}
            src={`https://www.youtube-nocookie.com/embed/${item.video_id}?autoplay=1&rel=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="px-5 py-3 flex items-center justify-end">
          <button onClick={() => openExternal(`https://www.youtube.com/watch?v=${item.video_id}`)} data-testid="stream-watch-youtube" className="font-body text-xs text-zinc-400 hover:text-[#14A89A] flex items-center gap-1.5 transition-colors">
            <ExternalLink size={13} /> Open on YouTube
          </button>
        </div>
      </div>
    </div>
  );
};

// Renders a shared stream reference inside a Signal post or a DM bubble.
export const StreamRefCard = ({ streamRef, compact }) => {
  const { openExternal } = useExternalLink();
  const [broken, setBroken] = useState(false);
  const [playing, setPlaying] = useState(false);
  const isVideo = streamRef.kind === "video" && streamRef.video_id;

  const open = () => { if (isVideo) setPlaying(true); else if (streamRef.url) openExternal(streamRef.url); };

  return (
    <>
      <button onClick={open} data-testid="stream-ref-card" className={`group block w-full text-left border transition-colors ${isVideo ? "border-white/10 hover:border-[#14A89A]/50 bg-[#0F0F0F]" : "border-white/10 hover:border-[#A855F7]/50 bg-[#101010]"}`}>
        {isVideo ? (
          <div className="relative aspect-video overflow-hidden bg-[#141414]">
            {streamRef.thumbnail && !broken ? (
              <img src={streamThumb(streamRef.thumbnail)} alt="" loading="lazy" onError={() => setBroken(true)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F2A28] to-[#141414]"><Radio size={26} className="text-[#14A89A]/40" /></div>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors">
              <span className="w-11 h-11 rounded-full bg-black/60 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play size={16} className="text-white fill-white ml-0.5" />
              </span>
            </div>
          </div>
        ) : (
          <div className="h-1 bg-gradient-to-r from-[#A855F7] to-[#14A89A]" />
        )}
        <div className={compact ? "p-2.5" : "p-3.5"}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`font-body text-[10px] tracking-[0.2em] uppercase truncate ${isVideo ? "text-[#14A89A]" : "text-[#A855F7]"}`}>{streamRef.source_name}</span>
            {isVideo
              ? <span className="font-body text-[9px] tracking-widest uppercase text-zinc-600 shrink-0 flex items-center gap-1"><Radio size={11} /> Stream</span>
              : <ExternalLink size={12} className="text-zinc-600 group-hover:text-[#A855F7] shrink-0 transition-colors" />}
          </div>
          <h4 className={`font-heading text-white leading-snug ${compact ? "text-xs line-clamp-2" : "text-sm line-clamp-2"}`}>{streamRef.title}</h4>
          {!compact && !isVideo && streamRef.excerpt && <p className="font-body text-xs text-zinc-500 leading-relaxed line-clamp-2 mt-1.5">{streamRef.excerpt}</p>}
        </div>
      </button>
      {playing && <StreamPlayerModal item={{ ...streamRef }} onClose={() => setPlaying(false)} />}
    </>
  );
};

// Share a public stream item INTO the E2E app: as a realm post, or a private DM.
export const StreamShareModal = ({ item, onClose }) => {
  const { encryptNetText, encryptDm, hasNetwork } = useCrypto();
  const [tab, setTab] = useState("realm"); // realm | dm
  const [realm, setRealm] = useState([]);
  const [postToFeed, setPostToFeed] = useState(true); // true = community feed + my stream; false = my stream only
  const [caption, setCaption] = useState("");
  const [dmText, setDmText] = useState("");
  const [members, setMembers] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const ref = toStreamRef(item);

  useEffect(() => {
    if (tab === "dm" && members === null) {
      api.get("/members").then(({ data }) => setMembers(data)).catch(() => setMembers([]));
    }
    /* eslint-disable-next-line */
  }, [tab]);

  const shareToRealm = async () => {
    if (postToFeed && !realm.length) return;
    setBusy(true);
    const payload = { stream_ref: ref, stream_personal: !postToFeed };
    if (realm.length) { payload.category = realm[0]; payload.categories = realm; }
    if (caption.trim() && hasNetwork()) {
      const enc = await encryptNetText(caption.trim());
      payload.caption_ct = enc.ct; payload.caption_iv = enc.iv; payload.enc = true; payload.caption = "";
    } else {
      payload.caption = caption.trim();
    }
    try {
      await api.post("/posts", payload);
      setDone(postToFeed
        ? "Shared to the realm, and saved to your Conscious Stream."
        : "Saved to your Conscious Stream on your profile.");
    }
    catch { alert("Could not share. Please try again."); }
    setBusy(false);
  };

  const sendTo = async (memberId) => {
    setBusy(true);
    let payload = { to_user_id: memberId, stream_ref: ref };
    if (dmText.trim()) {
      try { const enc = await encryptDm(memberId, dmText.trim()); payload = { ...payload, ...enc }; } catch { /* send without note */ }
    }
    try { await api.post("/messages", payload); setDone("Sent privately."); }
    catch { alert("Could not send. Please try again."); }
    setBusy(false);
  };

  const filtered = (members || []).filter((m) => matchesMember(m, search));

  return (
    <div className="fixed inset-0 z-[140] bg-black/85 flex items-center justify-center p-4" data-testid="stream-share-modal" onClick={onClose}>
      <div className="bg-[#121212] border border-white/10 max-w-md w-full p-6 fade-up max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-white flex items-center gap-2"><Share2 size={18} className="text-[#14A89A]" /> Share to the Signal</h3>
          <button onClick={onClose} data-testid="stream-share-close" className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="border border-white/10 bg-[#0D0D0D] p-2.5 mb-5 flex items-center gap-3">
          <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#14A89A]/10 border border-[#14A89A]/20">
            {item.kind === "video" ? <Play size={15} className="text-[#14A89A] fill-[#14A89A]" /> : <ExternalLink size={15} className="text-[#A855F7]" />}
          </div>
          <div className="min-w-0">
            <div className="font-body text-[10px] tracking-[0.2em] uppercase text-[#14A89A] truncate">{item.source_name}</div>
            <div className="font-heading text-xs text-white truncate">{item.title}</div>
          </div>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="stream-share-done">
            <p className="font-body text-sm text-[#14A89A] mb-6">{done}</p>
            <button onClick={onClose} className="bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#2BC4B5] transition-colors font-body">Done</button>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-5">
              <button onClick={() => setTab("realm")} data-testid="stream-share-tab-realm" className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-body text-xs tracking-wider uppercase border transition-colors ${tab === "realm" ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                <Repeat2 size={15} /> To a realm
              </button>
              <button onClick={() => setTab("dm")} data-testid="stream-share-tab-dm" className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 font-body text-xs tracking-wider uppercase border transition-colors ${tab === "dm" ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                <Send size={15} /> To a member
              </button>
            </div>

            {tab === "realm" ? (
              <div className="flex flex-col min-h-0 flex-1">
                <div className="grid grid-cols-2 gap-2 mb-4" data-testid="stream-share-destination">
                  <button onClick={() => setPostToFeed(true)} data-testid="stream-dest-feed" className={`px-3 py-2.5 font-body text-xs border transition-colors ${postToFeed ? "border-[#14A89A] text-[#14A89A] bg-[#14A89A]/[0.06]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                    Post to the community feed
                  </button>
                  <button onClick={() => setPostToFeed(false)} data-testid="stream-dest-personal" className={`px-3 py-2.5 font-body text-xs border transition-colors ${!postToFeed ? "border-[#14A89A] text-[#14A89A] bg-[#14A89A]/[0.06]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                    Just save to my stream
                  </button>
                </div>
                {postToFeed ? (
                  <>
                    <p className="font-body text-sm text-zinc-400 mb-4">Post this into a realm so sovereigns can gather and discuss it, and it's saved to your Conscious Stream too.</p>
                    <div className="overflow-y-auto flex-1 -mr-1 pr-1 mb-4">
                      <DomainPicker value={realm} onChange={setRealm} max={1} />
                    </div>
                  </>
                ) : (
                  <p className="font-body text-sm text-zinc-400 mb-4" data-testid="stream-personal-note">Saved privately to your <span className="text-[#14A89A]">Conscious Stream</span> on your profile, not posted to the community feed.</p>
                )}
                <textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add your words (optional, encrypted)" data-testid="stream-share-caption" rows={2} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none mb-3" />
                <button onClick={shareToRealm} disabled={busy || (postToFeed && !realm.length)} data-testid="stream-share-realm-submit" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3.5 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : (postToFeed
                    ? <><Repeat2 size={16} /> {realm.length ? `Share to ${CATEGORY_LABELS[realm[0]]}` : "Pick a realm"}</>
                    : <><Radio size={16} /> Save to my stream</>)}
                </button>
              </div>
            ) : (
              <div className="flex flex-col min-h-0 flex-1">
                <textarea value={dmText} onChange={(e) => setDmText(e.target.value)} placeholder="Add a private message (optional)" data-testid="stream-share-dm-note" rows={2} className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-none mb-3" />
                <div className="flex items-center gap-2 border-b border-white/15 mb-2">
                  <Search size={15} className="text-zinc-500" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a member" data-testid="stream-share-member-search" className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none font-body" />
                </div>
                <div className="overflow-y-auto flex-1 -mx-1">
                  {members === null ? (
                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-500" size={18} /></div>
                  ) : filtered.length === 0 ? (
                    <p className="font-body text-xs text-zinc-600 px-1 py-4">No members found.</p>
                  ) : filtered.map((m) => (
                    <button key={m.id} onClick={() => sendTo(m.id)} disabled={busy} data-testid={`stream-share-to-${m.id}`} className="w-full flex items-center gap-3 px-1 py-2.5 hover:bg-white/5 transition-colors text-left disabled:opacity-40">
                      <Avatar name={m.name} src={m.avatar} size={36} />
                      <div className="min-w-0">
                        <div className="font-heading text-sm text-white truncate">{m.name}</div>
                        <div className="font-body text-xs text-zinc-500 truncate">{m.headline}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
